import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError, fileUrl, thumbUrl } from "@/lib/api";
import { cacheGet, cachedFetch } from "@/lib/cache";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, ChevronLeft, Pencil, CalendarDays, CheckCircle2, RotateCcw, Trash2, Image as ImageIcon, Lock, Search, X, StickyNote } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { fechaSolo, ELIMINAR_MODELOS_HABILITADO } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";
import { Checkbox } from "@/components/ui/checkbox";
import NavFlechas from "@/components/NavFlechas";
import { etiquetaPaso } from "@/lib/pasoLabel";
import { compararModelos } from "@/lib/orden";

import { useConfirm } from "@/context/ConfirmContext";
import FechaInput from "@/components/FechaInput";
import RutaHormaCampos, { useRutas, rutaListo } from "@/components/RutaHormaCampos";
// COMPUERTAS por fase (PROMPT XX): 6 recuadros de progreso, colores de fase compartidos con Dashboard/Kanban/data cards.
const FASES_COMPUERTA = [
  { key: "Desarrollo · Horma", label: "Horma", color: "#a855f7" },
  { key: "Diseño", label: "Diseño", color: "#8b5cf6" },
  { key: "Prototipo", label: "Prototipo", color: "#3b82f6" },
  { key: "Muestra", label: "Muestra", color: "#f59e0b" },
  { key: "Cierre", label: "Cierre", color: "#0ea5e9" },
  { key: "Alta de Producto", label: "Alta", color: "#ec4899" },
];

// Data cards de etapa (mismo criterio de "etapa actual" del Dashboard). Completo = Liberado o Depurado.
const ETAPAS = [
  { key: "Desarrollo · Horma", label: "Desarrollo · Horma", color: "#a855f7" },
  { key: "Diseño", label: "Diseño", color: "#8b5cf6" },
  { key: "Prototipo", label: "Desarrollo · Prototipo", color: "#3b82f6" },
  { key: "Muestra", label: "Desarrollo · Muestra", color: "#f59e0b" },
  { key: "Cierre", label: "Desarrollo · Cierre", color: "#0ea5e9" },
  { key: "Alta de Producto", label: "Alta de Producto · Por liberar", color: "#ec4899" },
  { key: "Liberado", label: "Liberado", color: "#14b8a6" },
  { key: "Depurado", label: "Depurados", color: "#64748b" },
];

const esHoy = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return false;
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};
const ultimoDeFase = (m, fase) => {
  const ps = (m.pasos || []).filter((p) => p.fase === fase);
  if (!ps.length) return null;
  return ps.reduce((a, b) => (b.numero > a.numero ? b : a));
};

export default function ColeccionDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirmar = useConfirm();
  const [col, setCol] = useState(() => cacheGet(`/colecciones/${id}`) ?? null);
  const [modelos, setModelos] = useState(() => cacheGet(`/modelos?coleccion_id=${id}`) ?? null);
  const [listas, setListas] = useState(() => cacheGet("/listas") ?? {});
  const [disenadores, setDisenadores] = useState(() => cacheGet("/disenadores") ?? []);
  const [colList, setColList] = useState(() => cacheGet("/colecciones") ?? []);
  const [filtros, setFiltros] = useState({ categoria: "all", genero: "all", estrategia: "all", estatus: "all", ciclo_vida: "all" });
  const [busqueda, setBusqueda] = useState("");
  const [busquedaDebounced, setBusquedaDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setBusquedaDebounced(busqueda), 300);
    return () => clearTimeout(t);
  }, [busqueda]);
  const [open, setOpen] = useState(false);
  const emptyForm = {
    nombre: "", sku: "", ruta: "", horma_id: "",
    material: "", suela: "",
    categoria: "", genero: "", estrategia: "", ciclo_vida: "", linea: "", proveedor: "",
    costo_meta: "", drop: "", compra_inicial: "",
    fecha_meta_prototipo: "", notas_desarrollo: "",
  };
  const [form, setForm] = useState(emptyForm);
  const rutas = useRutas();
  const conPrototipo = !form.ruta || rutas.find((r) => r.id === form.ruta)?.con_prototipo !== false;
  const [busy, setBusy] = useState(false);
  const [openCol, setOpenCol] = useState(false);
  const [colForm, setColForm] = useState({ nombre: "", temporada: "", responsable: "", fecha_inicio: "", fecha_termino: "" });

  const abrirEditCol = () => {
    setColForm({
      nombre: col.nombre, temporada: col.temporada, responsable: col.responsable || "",
      fecha_inicio: col.fecha_inicio || "", fecha_termino: col.fecha_termino || "",
    });
    setOpenCol(true);
  };
  const guardarCol = async () => {
    setBusy(true);
    try {
      await api.put(`/colecciones/${id}`, colForm);
      toast.success("Colección actualizada");
      setOpenCol(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const completar = async () => {
    try {
      await api.post(`/colecciones/${id}/completar`);
      toast.success("Colección completada");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reabrir = async () => {
    try {
      await api.post(`/colecciones/${id}/reabrir`);
      toast.success("Colección reabierta");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delModelo = async (m, e) => {
    e.stopPropagation();
    if (!(await confirmar({ titulo: `¿Eliminar el modelo "${m.nombre}"?`, mensaje: "El modelo se archiva: sale de la colección, Kanban y Dashboard, pero su ficha, documentos e historial se conservan.\n\nEsto no es depurar. Para descontinuar un modelo sin terminarlo, usa Acciones → Depurar dentro de su ficha.", accion: "Eliminar modelo", peligro: true }))) return;
    try {
      await api.delete(`/modelos/${m.id}`);
      toast.success("Modelo eliminado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  // Foto para la tabla (vista de lista): usa la MINIATURA cuando hay file_id (más ligera).
  const fotoSrc = (m) => {
    const mm = m.multimedia || {};
    if (mm.foto_final_file_id) return thumbUrl(mm.foto_final_file_id);
    if (mm.foto_final_url) return mm.foto_final_url;
    if (mm.boceto_file_id) return thumbUrl(mm.boceto_file_id);
    if (mm.boceto_url) return mm.boceto_url;
    return null;
  };

  const load = () => {
    cachedFetch(`/colecciones/${id}`, setCol).catch(() => navigate("/colecciones"));
    cachedFetch(`/modelos?coleccion_id=${id}`, setModelos);
  };
  useEffect(() => {
    load();
    cachedFetch("/listas", setListas);
    cachedFetch("/disenadores", setDisenadores).catch(() => {});
    cachedFetch("/colecciones", setColList).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const filtrados = useMemo(() => {
    if (!modelos) return [];
    const q = busquedaDebounced.trim().toLowerCase();
    const lista = modelos.filter((m) => {
      if (filtros.categoria !== "all" && m.organizacion.categoria !== filtros.categoria) return false;
      if (filtros.genero !== "all" && m.organizacion.genero !== filtros.genero) return false;
      if (filtros.estrategia !== "all" && m.organizacion.estrategia !== filtros.estrategia) return false;
      if (filtros.ciclo_vida !== "all" && m.organizacion.ciclo_vida !== filtros.ciclo_vida) return false;
      if (filtros.estatus !== "all") {
        if (filtros.estatus === "Depurado") {
          if (!m.depurado) return false;
        } else {
          const fase = m.estatus.liberado ? "Liberado" : m.estatus.siguiente_paso?.fase;
          if (m.depurado || fase !== filtros.estatus) return false;
        }
      }
      if (q && !`${m.nombre || ""} ${m.sku || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
    // Orden: pendientes (A-Z) -> completados/liberados (A-Z) -> depurados (A-Z) al final.
    return lista.sort(compararModelos);
  }, [modelos, filtros, busquedaDebounced]);

  // Resumen por etapa de TODA la colección (no depende de filtros/buscador). "Nuevo +N" = cruzó a esa etapa HOY.
  const resumenEtapas = useMemo(() => {
    const base = {};
    ETAPAS.forEach((e) => (base[e.key] = { total: 0, nuevo: 0 }));
    (modelos || []).forEach((m) => {
      if (m.depurado) {
        base["Depurado"].total += 1;
        if (esHoy(m.fecha_depurado)) base["Depurado"].nuevo += 1;
        return;
      }
      if (m.estatus?.liberado) {
        base["Liberado"].total += 1;
        const p24 = (m.pasos || []).find((p) => p.numero === 24);
        if (p24?.hecho && esHoy(p24.completado_en)) base["Liberado"].nuevo += 1;
        return;
      }
      const etapa = m.estatus?.siguiente_paso?.fase;
      if (!etapa || !base[etapa]) return;
      base[etapa].total += 1;
      let nuevo = false;
      if (etapa === "Desarrollo · Horma") {
        nuevo = esHoy(m.created_at); // primera fase: solicitud creada hoy
      } else if (etapa === "Diseño") {
        const p = ultimoDeFase(m, "Desarrollo · Horma"); // Tabla de medidas horma
        nuevo = !!(p?.hecho && esHoy(p.completado_en));
      } else if (etapa === "Prototipo") {
        const p = ultimoDeFase(m, "Diseño"); // Carta proto
        nuevo = !!(p?.hecho && esHoy(p.completado_en));
      } else if (etapa === "Muestra") {
        const p = ultimoDeFase(m, "Prototipo"); // Prototipo validado
        nuevo = !!(p?.hecho && esHoy(p.completado_en));
      } else if (etapa === "Cierre") {
        const p = ultimoDeFase(m, "Muestra"); // Adorno (Muestra)
        nuevo = !!(p?.hecho && esHoy(p.completado_en));
      } else if (etapa === "Alta de Producto") {
        // Entró a Alta de Producto al completar el último paso del desarrollo (1–21); si fue hoy, es nuevo.
        const fechas = (m.pasos || []).filter((p) => p.numero <= 21 && p.hecho && p.completado_en).map((p) => p.completado_en).sort();
        nuevo = esHoy(fechas[fechas.length - 1]);
      }
      if (nuevo) base[etapa].nuevo += 1;
    });
    return base;
  }, [modelos]);

  const completada = col?.estado === "completada";
  const todos100 = modelos && modelos.length > 0 && modelos.every((m) => m.depurado || m.estatus.completo);
  const puedeCompletar = user?.es_admin || user?.perms?.acciones?.completar_colecciones;
  const puedeReabrir = user?.es_admin || user?.perms?.acciones?.reabrir_colecciones;
  const puedeEliminarModelo = ELIMINAR_MODELOS_HABILITADO && (user?.es_admin || user?.perms?.acciones?.eliminar_modelos);
  const puedeOrgBulk = user?.es_admin || (user?.perms?.secciones || []).includes("organizacion");
  const puedeDisenadorBulk = user?.es_admin || !!user?.perms?.acciones?.asignar_disenador;
  const puedeDepurarBulk = user?.es_admin || !!user?.perms?.acciones?.hacer_depurado;
  const puedeSelMasiva = user?.es_admin || !!user?.perms?.acciones?.seleccion_masiva;
  const [sel, setSel] = useState(() => new Set());
  // Las casillas de selección masiva solo aparecen con el botón "Seleccionar" (ganan espacio en la tabla).
  const [modoSel, setModoSel] = useState(false);
  const mostrarSel = puedeSelMasiva && modoSel;
  const alternarModoSel = () => { if (modoSel) setSel(new Set()); setModoSel(!modoSel); };
  const [bulk, setBulk] = useState(null);
  const [bulkValue, setBulkValue] = useState("");
  const [bulkMotivo, setBulkMotivo] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const selIds = [...sel];
  const reloadModelos = () => cachedFetch(`/modelos?coleccion_id=${id}`, setModelos);
  const toggleSel = (mid, e) => { e.stopPropagation(); setSel((prev) => { const n = new Set(prev); n.has(mid) ? n.delete(mid) : n.add(mid); return n; }); };
  const BULK_LABELS = { disenador: "Cambiar diseñador", coleccion: "Cambiar colección", categoria: "Cambiar categoría", genero: "Cambiar género", estrategia: "Cambiar estrategia", ciclo_vida: "Cambiar ciclo de vida", linea: "Cambiar línea", proveedor: "Cambiar proveedor/maquilador", depurar: "Depurar modelos" };
  const BULK_SHORT = { categoria: "Categoría", genero: "Género", estrategia: "Estrategia", ciclo_vida: "Ciclo de vida", linea: "Línea", proveedor: "Proveedor" };
  const abrirBulk = (a) => { setBulkValue(""); setBulkMotivo(""); setBulk(a); };
  const aplicarBulk = async () => {
    setBulkBusy(true);
    try {
      let res;
      if (bulk === "coleccion") res = await api.post("/modelos-bulk/reasignar", { ids: selIds, coleccion_id: bulkValue });
      else if (bulk === "depurar") res = await api.post("/modelos-bulk/depurado", { ids: selIds, motivo: bulkMotivo.trim() });
      else { const key = bulk === "disenador" ? "disenador_id" : bulk; res = await api.post("/modelos-bulk/organizacion", { ids: selIds, campos: { [key]: bulkValue } }); }
      const n = res?.data?.actualizados ?? selIds.length;
      toast.success(`Cambios aplicados a ${n} modelo(s)`);
      setBulk(null); setSel(new Set()); reloadModelos();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };
  const puedeCrearModelo = user?.es_admin || user?.perms?.acciones?.crear_modelos_coleccion;
  const disMap = useMemo(() => Object.fromEntries((disenadores || []).map((u) => [u.id, u.nombre])), [disenadores]);
  const colSpanVacio = 7 + (mostrarSel ? 1 : 0) + (puedeEliminarModelo ? 1 : 0);
  const idxCol = (colList || []).findIndex((c) => c.id === id);
  const irColeccion = (dir) => {
    const t = colList[idxCol + dir];
    if (t) navigate(`/colecciones/${t.id}`);
  };

  const crear = async () => {
    setBusy(true);
    try {
      await api.post("/modelos", {
        coleccion_id: id,
        nombre: form.nombre,
        sku: form.sku,
        ruta: form.ruta, horma_id: form.horma_id || null,
        ficha_tecnica: { material: form.material, suela: form.suela },
        organizacion: { categoria: form.categoria, genero: form.genero, estrategia: form.estrategia, ciclo_vida: form.ciclo_vida, linea: form.linea, proveedor: form.proveedor },
        comercial: {
          costo_meta: form.costo_meta === "" ? null : Number(form.costo_meta),
          drop: form.drop,
          compra_inicial: form.compra_inicial === "" ? null : Number(form.compra_inicial),
        },
        notas_desarrollo: form.notas_desarrollo,
        fecha_meta_prototipo: conPrototipo ? form.fecha_meta_prototipo || null : null,
      });
      toast.success("Modelo creado");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const Drop = ({ campo, label }) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={form[campo]} onValueChange={(v) => setForm({ ...form, [campo]: v })}>
        <SelectTrigger data-testid={`modelo-${campo}-input`}><SelectValue placeholder="Selecciona" /></SelectTrigger>
        <SelectContent>{(listas[campo]?.valores || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  const Filtro = ({ campo, label, opts }) => (
    <Select value={filtros[campo]} onValueChange={(v) => setFiltros({ ...filtros, [campo]: v })}>
      <SelectTrigger className="w-[150px] h-9 bg-white" data-testid={`filtro-${campo}`}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{label}: Todos</SelectItem>
        {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="p-6 lg:p-8">
      <Link to="/colecciones" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4 transition-colors">
        <ChevronLeft size={16} /> Colecciones
      </Link>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-head text-3xl font-extrabold tracking-tight">{col?.nombre || "…"}</h1>
            {col && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">{col.temporada}</span>}
            {completada && <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-800" data-testid="coleccion-completada-badge"><Lock size={11} /> Completada</span>}
            {user?.es_admin && col && !completada && (
              <button onClick={abrirEditCol} className="text-slate-400 hover:text-blue-600 p-1 transition-colors" data-testid="edit-coleccion-button"><Pencil size={14} /></button>
            )}
            {colList.length > 1 && idxCol >= 0 && (
              <NavFlechas
                testid="coleccion-nav"
                labelPrev="Colección anterior"
                labelNext="Colección siguiente"
                onPrev={() => irColeccion(-1)}
                onNext={() => irColeccion(1)}
                prevDisabled={idxCol <= 0}
                nextDisabled={idxCol >= colList.length - 1}
              />
            )}
          </div>
          <p className="text-slate-500 mt-1">
            Vista de colección — una fila por modelo, con el avance de las compuertas de su ruta.
          </p>
          {col && (col.fecha_inicio || col.fecha_termino) && (
            <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-1.5">
              <CalendarDays size={13} /> {fechaSolo(col.fecha_inicio)} → {fechaSolo(col.fecha_termino)}
            </div>
          )}
        </div>
        {user && (
          <div className="flex items-center gap-2 flex-wrap">
            {puedeCompletar && !completada && (
              <Button variant="outline" className="rounded-lg" disabled={!todos100} onClick={completar} data-testid="completar-coleccion-button" title={todos100 ? "" : "Todos los modelos deben estar al 100%"}>
                <CheckCircle2 size={16} className="mr-1" /> Completar colección
              </Button>
            )}
            {puedeReabrir && completada && (
              <Button variant="outline" className="rounded-lg" onClick={reabrir} data-testid="reabrir-coleccion-button">
                <RotateCcw size={16} className="mr-1" /> Reabrir colección
              </Button>
            )}
            {puedeCrearModelo && !completada && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-lg" data-testid="new-modelo-button"><Plus size={16} className="mr-1" /> Nuevo modelo</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto thin-scroll">
              <DialogHeader><DialogTitle>Nuevo modelo</DialogTitle></DialogHeader>
              <div className="space-y-5 py-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Identidad</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Nombre del modelo *</Label>
                      <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="modelo-nombre-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>SKU <span className="text-slate-400 font-normal">(opcional)</span></Label>
                      <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="Se define después" data-testid="modelo-sku-input" />
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Ruta de desarrollo</p>
                  <RutaHormaCampos value={{ ruta: form.ruta, horma_id: form.horma_id }} onChange={(v) => setForm({ ...form, ...v })} testid="modelo-ruta" />
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Ficha técnica</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Material (upper)</Label><Input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} data-testid="modelo-material-input" /></div>
                    <div className="space-y-1.5"><Label>Suela</Label><Input value={form.suela} onChange={(e) => setForm({ ...form, suela: e.target.value })} data-testid="modelo-suela-input" /></div>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Organización del producto</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Drop campo="categoria" label="Categoría" />
                    <Drop campo="genero" label="Género" />
                    <Drop campo="estrategia" label="Estrategia" />
                    <Drop campo="ciclo_vida" label="Ciclo de vida" />
                    <div className="space-y-1.5"><Label>Línea</Label><Input value={form.linea} onChange={(e) => setForm({ ...form, linea: e.target.value })} data-testid="modelo-linea-input" /></div>
                    <div className="space-y-1.5"><Label>Proveedor / maquilador</Label><Input value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} data-testid="modelo-proveedor-input" /></div>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Comercial</p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5"><Label>Costo meta (MXN)</Label><Input type="number" value={form.costo_meta} onChange={(e) => setForm({ ...form, costo_meta: e.target.value })} data-testid="modelo-costo-input" /></div>
                    <div className="space-y-1.5"><Label>Drop</Label><Input value={form.drop} onChange={(e) => setForm({ ...form, drop: e.target.value })} data-testid="modelo-drop-input" /></div>
                    <div className="space-y-1.5"><Label>Compra inicial</Label><Input type="number" value={form.compra_inicial} onChange={(e) => setForm({ ...form, compra_inicial: e.target.value })} data-testid="modelo-compra-input" /></div>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Planeación y notas</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {conPrototipo && <div className="space-y-1.5"><Label>Fecha meta del prototipo</Label><FechaInput value={form.fecha_meta_prototipo} onChange={(e) => setForm({ ...form, fecha_meta_prototipo: e.target.value })} data-testid="modelo-meta-input" /></div>}
                  </div>
                  <div className="space-y-1.5 mt-3"><Label>Notas de desarrollo</Label><Textarea rows={3} value={form.notas_desarrollo} onChange={(e) => setForm({ ...form, notas_desarrollo: e.target.value })} data-testid="modelo-notas-input" /></div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={crear} disabled={busy || !form.nombre.trim() || !rutaListo(rutas, form)} data-testid="modelo-save-button">Crear modelo</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
            )}
          </div>
        )}
      </div>

      <Dialog open={openCol} onOpenChange={setOpenCol}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar colección</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={colForm.nombre} onChange={(e) => setColForm({ ...colForm, nombre: e.target.value })} data-testid="edit-coleccion-nombre" />
            </div>
            <div className="space-y-1.5">
              <Label>Temporada</Label>
              <Input value={colForm.temporada} onChange={(e) => setColForm({ ...colForm, temporada: e.target.value })} data-testid="edit-coleccion-temporada" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Fecha de inicio</Label>
                <FechaInput value={colForm.fecha_inicio} onChange={(e) => setColForm({ ...colForm, fecha_inicio: e.target.value })} data-testid="edit-coleccion-inicio" />
              </div>
              <div className="space-y-1.5">
                <Label>Fecha de término</Label>
                <FechaInput value={colForm.fecha_termino} onChange={(e) => setColForm({ ...colForm, fecha_termino: e.target.value })} data-testid="edit-coleccion-termino" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={guardarCol} disabled={busy || !colForm.nombre || !colForm.temporada} data-testid="edit-coleccion-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {modelos && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="resumen-etapas">
          {ETAPAS.map((e) => {
            const r = resumenEtapas[e.key] || { total: 0, nuevo: 0 };
            const activa = filtros.estatus === e.key;
            // Click = filtra la tabla por esa etapa; otro click (o el filtro "Etapa") lo quita o lo cambia.
            return (
              <Card
                key={e.key}
                role="button"
                aria-pressed={activa}
                title={activa ? "Quitar filtro" : "Filtrar por esta etapa"}
                onClick={() => setFiltros({ ...filtros, estatus: activa ? "all" : e.key })}
                className={`p-4 shadow-sm cursor-pointer select-none transition-all hover:shadow-md ${activa ? "ring-2 ring-offset-1" : "border-slate-200"}`}
                style={activa ? { borderColor: e.color, "--tw-ring-color": e.color } : undefined}
                data-testid={`etapa-card-${e.key}`}
              >
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: e.color }} />
                  <span className="text-xs font-semibold text-slate-500 leading-tight">{e.label}</span>
                </div>
                <div className="mt-2 text-3xl font-extrabold tabular-nums text-slate-800" data-testid={`etapa-total-${e.key}`}>{r.total}</div>
                {r.nuevo > 0 && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`etapa-nuevo-${e.key}`}>
                    Nuevo +{r.nuevo}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative w-full sm:w-64">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar modelo o SKU…"
            className="h-9 pl-9 pr-8 bg-white"
            data-testid="buscar-modelo-input"
          />
          {busqueda && (
            <button onClick={() => setBusqueda("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="buscar-modelo-clear" aria-label="Limpiar búsqueda">
              <X size={15} />
            </button>
          )}
        </div>
        <Filtro campo="categoria" label="Categoría" opts={listas.categoria?.valores || []} />
        <Filtro campo="genero" label="Género" opts={listas.genero?.valores || []} />
        <Filtro campo="estrategia" label="Estrategia" opts={listas.estrategia?.valores || []} />
        <Filtro campo="ciclo_vida" label="Ciclo de vida" opts={listas.ciclo_vida?.valores || []} />
        <Filtro campo="estatus" label="Etapa" opts={["Desarrollo · Horma", "Diseño", "Prototipo", "Muestra", "Cierre", "Alta de Producto", "Liberado", "Depurado"]} />
        {puedeSelMasiva && (
          <Button size="sm" variant={modoSel ? "default" : "outline"} className="h-9 ml-auto" onClick={alternarModoSel} data-testid="modo-seleccion">
            {modoSel ? "Terminar selección" : "Seleccionar"}
          </Button>
        )}
      </div>

      {mostrarSel && selIds.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-3 p-3 rounded-xl border border-blue-200 bg-blue-50" data-testid="bulk-bar">
          <span className="font-semibold text-sm text-blue-900" data-testid="bulk-count">{selIds.length} seleccionados</span>
          <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => setSel(new Set())} data-testid="bulk-clear">Limpiar</Button>
          <div className="w-px h-5 bg-blue-200" />
          {puedeDisenadorBulk && <Button size="sm" variant="outline" className="h-8" onClick={() => abrirBulk("disenador")} data-testid="bulk-disenador">Diseñador</Button>}
          <Button size="sm" variant="outline" className="h-8" onClick={() => abrirBulk("coleccion")} data-testid="bulk-coleccion">Colección</Button>
          {puedeOrgBulk && ["categoria", "genero", "estrategia", "ciclo_vida", "linea", "proveedor"].map((f) => (
            <Button key={f} size="sm" variant="outline" className="h-8" onClick={() => abrirBulk(f)} data-testid={`bulk-${f}`}>{BULK_SHORT[f]}</Button>
          ))}
          {puedeDepurarBulk && <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => abrirBulk("depurar")} data-testid="bulk-depurar">Depurar</Button>}
        </div>
      )}

      <Dialog open={!!bulk} onOpenChange={(o) => { if (!o) setBulk(null); }}>
        <DialogContent data-testid="bulk-dialog">
          <DialogTitle>{bulk ? BULK_LABELS[bulk] : ""}</DialogTitle>
          <p className="text-sm text-slate-500 -mt-2">Se aplicará a {selIds.length} modelo(s) seleccionado(s).</p>
          <div className="space-y-2 py-1">
            {bulk === "depurar" ? (
              <div className="space-y-1.5">
                <Label>Motivo (obligatorio)</Label>
                <Textarea rows={3} value={bulkMotivo} onChange={(e) => setBulkMotivo(e.target.value)} placeholder="Motivo (mínimo 10 caracteres)…" data-testid="bulk-motivo" />
                <p className="text-[11px] text-slate-400">{bulkMotivo.trim().length}/10</p>
              </div>
            ) : bulk === "linea" || bulk === "proveedor" ? (
              <Input value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder={BULK_LABELS[bulk]} data-testid="bulk-value" />
            ) : (
              <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} data-testid="bulk-value">
                <option value="">Selecciona…</option>
                {bulk === "disenador" && disenadores.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                {bulk === "coleccion" && colList.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                {["categoria", "genero", "estrategia", "ciclo_vida"].includes(bulk) && (listas[bulk]?.valores || []).map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulk(null)} data-testid="bulk-cancel">Cancelar</Button>
            <Button onClick={aplicarBulk} disabled={bulkBusy || (bulk === "depurar" ? bulkMotivo.trim().length < 10 : !bulkValue)} data-testid="bulk-apply">Aplicar a {selIds.length}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!modelos ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : (
        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/80 text-left text-xs uppercase tracking-wide text-slate-500">
                  {mostrarSel && <th className="px-3 py-3 w-8"><Checkbox className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white" checked={filtrados.length > 0 && filtrados.every((m) => sel.has(m.id))} onCheckedChange={() => setSel(filtrados.length > 0 && filtrados.every((m) => sel.has(m.id)) ? new Set() : new Set(filtrados.map((m) => m.id)))} data-testid="bulk-select-all" /></th>}
                  <th className="px-4 py-3 font-semibold sticky left-0 z-20 bg-slate-50 shadow-[1px_0_0_0_#e2e8f0]">Modelo</th>
                  <th className="px-2 py-3 font-semibold text-center" title="Notas de desarrollo del modelo (pasa el cursor para leerlas)">Notas</th>
                  <th className="px-3 py-3 font-semibold">Foto</th>
                  <th className="px-3 py-3 font-semibold" title="Categoría · Género · Estrategia">Clasificación</th>
                  <th className="px-3 py-3 font-semibold">Diseñador</th>
                  <th className="px-3 py-3 font-semibold text-center" title="Compuertas 1–21">Compuertas</th>
                  <th className="px-3 py-3 font-semibold">Estatus</th>
                  {puedeEliminarModelo && <th className="px-3 py-3 font-semibold text-right">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {filtrados.map((m) => (
                  <tr
                    key={m.id}
                    onClick={() => navigate(`/modelos/${m.id}`)}
                    className={`group border-b border-slate-100 cursor-pointer transition-colors ${sel.has(m.id) ? "bg-blue-50 hover:bg-blue-50" : "hover:bg-slate-50"}`}
                    data-testid={`modelo-row-${m.id}`}
                  >
                    {mostrarSel && <>
                    <td className={`px-3 py-3 cursor-pointer ${sel.has(m.id) ? "bg-blue-50" : ""}`} onClick={(e) => toggleSel(m.id, e)} data-testid={`bulk-select-${m.id}`}>
                      <Checkbox checked={sel.has(m.id)} className="pointer-events-none data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white" />
                    </td>
                    </>}
                    <td className={`px-4 py-3 sticky left-0 z-10 min-w-[200px] max-w-[260px] shadow-[1px_0_0_0_#e2e8f0] cursor-pointer transition-colors ${sel.has(m.id) ? "bg-blue-50" : "bg-white group-hover:bg-slate-50"}`} data-testid={`modelo-selrow-${m.id}`}>
                      <div className="font-semibold text-slate-800">
                        {m.nombre}
                      </div>
                      <div className="text-xs text-slate-400 font-mono">{m.sku || "sin SKU"}</div>
                    </td>
                    <td className="px-2 py-3 text-center" data-testid={`notas-${m.id}`}>
                      {m.notas_desarrollo?.trim() ? (
                        <HoverCard openDelay={100} closeDelay={100}>
                          <HoverCardTrigger asChild>
                            <span className="inline-flex p-1 rounded text-amber-500 hover:bg-amber-50" onClick={(e) => e.stopPropagation()} data-testid={`notas-icono-${m.id}`}><StickyNote size={16} /></span>
                          </HoverCardTrigger>
                          <HoverCardContent side="right" align="start" className="w-80 max-h-64 overflow-auto text-sm text-slate-700 whitespace-pre-wrap text-left" data-testid={`notas-texto-${m.id}`}>
                            {m.notas_desarrollo}
                          </HoverCardContent>
                        </HoverCard>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-3">
                      {fotoSrc(m) ? (
                        <LazyImage src={fotoSrc(m)} alt={m.nombre} containerClassName="h-10 w-10 rounded-md border border-slate-200 shrink-0" imgClassName="object-cover" testid={`modelo-foto-${m.id}`} />
                      ) : (
                        <div data-testid={`modelo-foto-${m.id}-placeholder`} data-state="empty" className="h-10 w-10 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-300"><ImageIcon size={16} /></div>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap" data-testid={`modelo-clasificacion-${m.id}`}>
                      <div className="text-slate-700">{m.organizacion.categoria || "—"}</div>
                      <div className="text-xs text-slate-400">{m.organizacion.genero || "—"} · {m.organizacion.estrategia || "—"}</div>
                    </td>
                    <td className="px-3 py-3">
                      {(() => {
                        const dis = disMap[m.organizacion?.disenador_id];
                        return dis ? (
                          <div
                            className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center font-semibold text-slate-700 text-sm"
                            title={dis}
                            data-testid={`modelo-disenador-${m.id}`}
                          >
                            {dis[0]?.toUpperCase()}
                          </div>
                        ) : (
                          <div
                            className="h-9 w-9 rounded-full bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-slate-300 text-sm"
                            title="Sin diseñador asignado"
                            data-testid={`modelo-disenador-${m.id}`}
                          >
                            —
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1 justify-center">
                        {FASES_COMPUERTA.map((f) => {
                          const pasosF = m.pasos.filter((x) => x.fase === f.key);
                          const total = pasosF.length;
                          const done = pasosF.filter((x) => x.hecho).length;
                          const pct = total ? Math.round((done / total) * 100) : 0;
                          return (
                            <div key={f.key} className="w-12" data-testid={`compuerta-fase-${m.id}-${f.label}`} title={`${f.label}: ${done}/${total} (${pct}%)`}>
                              <div className="text-[9px] text-slate-400 text-center mb-0.5 truncate">{f.label}</div>
                              <div className="relative h-4 rounded bg-slate-100 overflow-hidden">
                                <div className="absolute inset-y-0 left-0 rounded bg-emerald-600" style={{ width: `${pct}%` }} />
                                <span className={`absolute inset-0 flex items-center justify-center leading-none text-[9px] font-semibold ${pct >= 55 ? "text-white" : "text-slate-600"}`}>{pct}%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full bg-slate-800" style={{ width: `${m.estatus.porcentaje}%` }} />
                        </div>
                        <span className="tabular-nums text-xs font-semibold text-slate-600 w-9">{m.estatus.porcentaje}%</span>
                        {m.depurado ? (
                          <span className="inline-flex items-center whitespace-nowrap text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-700" data-testid={`estatus-pill-${m.id}`}>Depurado</span>
                        ) : m.estatus.liberado ? (
                          <span className="inline-flex items-center whitespace-nowrap text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700" data-testid={`estatus-pill-${m.id}`}>Liberado</span>
                        ) : m.estatus.desarrollo_completo ? (
                          <span className="inline-flex items-center whitespace-nowrap text-[10px] font-semibold px-2 py-0.5 rounded-full bg-pink-100 text-pink-700" data-testid={`estatus-pill-${m.id}`}>Por liberar</span>
                        ) : null}
                      </div>
                      {!(m.depurado || m.estatus.liberado) && (
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {m.estatus.siguiente_paso ? `Sigue: ${etiquetaPaso(m.estatus.siguiente_paso, m.pasos)}` : "Sin iniciar"}
                          <span className="block font-semibold text-slate-600 tabular-nums whitespace-nowrap" title="Días detenido en el paso actual pendiente (mismo cálculo que Cuellos de botella)" data-testid={`tiempo-etapa-${m.id}`}>
                            {m.dias_detenido} {m.dias_detenido === 1 ? "día hábil" : "días hábiles"}
                          </span>
                        </div>
                      )}
                    </td>
                    {puedeEliminarModelo && (
                      <td className="px-3 py-3 text-right">
                        <button onClick={(e) => delModelo(m, e)} className="text-slate-400 hover:text-red-600 p-1.5 transition-colors" data-testid={`delete-modelo-${m.id}`}><Trash2 size={15} /></button>
                      </td>
                    )}
                  </tr>
                ))}
                {filtrados.length === 0 && (
                  <tr><td colSpan={colSpanVacio} className="px-4 py-12 text-center text-slate-400" data-testid="modelos-sin-resultados">{busquedaDebounced.trim() ? "No se encontraron modelos para tu búsqueda" : "No hay modelos que coincidan con los filtros."}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
